import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { colors } from "brand";
import { useTranslation } from "i18n-fe";
import { userDisplayName } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";

// Card #1742 — create/edit one composite-judge mapping. PRIMARY and SECONDARY
// are picked explicitly from the show's roster (two separate Selects); never
// inferred from array order or parsed out of the composite judge's name (the
// card's scope rule — #1342's background is that "Williams Koch" is FVD
// packing two real judges into one roster row, not a name to split).
//
// Backend codes (apps/score-api/src/handlers/composite-judge-map.js) are
// mapped to curated, static i18n keys here rather than shown raw — same
// pattern as RideEditor's RESEND_ERROR_KEYS — since common-fe's
// installServerErrorInterceptor rewrites err.message to a generic string and
// stashes the real code on err.response.data.code.
const ERROR_KEYS = {
  primary_judge_not_found: "judges.compositeTab.errorPrimaryNotFound",
  secondary_judge_not_found: "judges.compositeTab.errorSecondaryNotFound",
  composite_judge_not_found: "judges.compositeTab.errorCompositeNotFound",
  duplicate_judge: "judges.compositeTab.errorDuplicateJudge",
  self_mapping: "judges.compositeTab.errorSelfMapping",
  already_mapped: "judges.compositeTab.errorAlreadyMapped",
};

export default function CompositeJudgeMapDialog({
  open,
  onClose,
  activeShowId,
  compositeJudge,
  existingMapping,
  judgeOptions,
  onSaved,
}) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();

  const [primaryJudgeId, setPrimaryJudgeId] = useState("");
  const [secondaryJudgeId, setSecondaryJudgeId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // This Dialog is already bumped above the AppBar (modal + 100, below); each
  // Select's own popover Menu defaults to zIndex.modal and would otherwise
  // render BEHIND the Dialog's container — same fix BulkAssignScribesDialog /
  // ClassDetailDrawer use for their pickers (menuProps at modal + 200, one
  // layer above this Dialog).
  const menuProps = { sx: { zIndex: (theme) => theme.zIndex.modal + 200 } };

  // Re-seed from the existing mapping (edit) or blank (create) every time the
  // dialog is opened for a (possibly different) composite judge.
  useEffect(() => {
    if (!open) return;
    setPrimaryJudgeId(existingMapping?.primaryJudgeId || "");
    setSecondaryJudgeId(existingMapping?.secondaryJudgeId || "");
    setError(null);
  }, [open, existingMapping]);

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const handleSave = async () => {
    if (!compositeJudge || !primaryJudgeId || !secondaryJudgeId) return;
    try {
      setSaving(true);
      setError(null);
      const saved = existingMapping
        ? unwrap(
            await scoreApi.judges.updateCompositeJudgeMap(compositeJudge.id, {
              primaryJudgeId,
              secondaryJudgeId,
            }),
          )
        : unwrap(
            await scoreApi.judges.createCompositeJudgeMap({
              showId: activeShowId,
              compositeJudgeId: compositeJudge.id,
              primaryJudgeId,
              secondaryJudgeId,
            }),
          );
      onSaved?.(saved);
    } catch (err) {
      const code = err?.response?.data?.code;
      setError({ message: t(ERROR_KEYS[code] || "judges.compositeTab.saveError") });
    } finally {
      setSaving(false);
    }
  };

  const canSave =
    !!primaryJudgeId && !!secondaryJudgeId && primaryJudgeId !== secondaryJudgeId && !saving;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      // AppLayout's AppBar (common-fe) is pinned at zIndex 1300 — same fix as
      // JudgeSignatureModal/RideHistoryDrawer.
      sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6" component="span" color="text.primary">
          {compositeJudge
            ? t(
                existingMapping
                  ? "judges.compositeTab.dialogTitleEdit"
                  : "judges.compositeTab.dialogTitleCreate",
                { name: userDisplayName(compositeJudge) },
              )
            : ""}
        </Typography>
        <IconButton onClick={handleClose} aria-label={t("judges.compositeTab.cancel")}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <FormControl size="small" fullWidth disabled={saving}>
            <InputLabel id="composite-primary-label">
              {t("judges.compositeTab.primaryLabel")}
            </InputLabel>
            <Select
              labelId="composite-primary-label"
              label={t("judges.compositeTab.primaryLabel")}
              value={primaryJudgeId}
              onChange={(e) => setPrimaryJudgeId(e.target.value)}
              MenuProps={menuProps}
            >
              {judgeOptions
                .filter((j) => j.id !== secondaryJudgeId)
                .map((j) => (
                  <MenuItem key={j.id} value={j.id}>
                    {userDisplayName(j)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
            {t("judges.compositeTab.primaryHint")}
          </Typography>

          <FormControl size="small" fullWidth disabled={saving}>
            <InputLabel id="composite-secondary-label">
              {t("judges.compositeTab.secondaryLabel")}
            </InputLabel>
            <Select
              labelId="composite-secondary-label"
              label={t("judges.compositeTab.secondaryLabel")}
              value={secondaryJudgeId}
              onChange={(e) => setSecondaryJudgeId(e.target.value)}
              MenuProps={menuProps}
            >
              {judgeOptions
                .filter((j) => j.id !== primaryJudgeId)
                .map((j) => (
                  <MenuItem key={j.id} value={j.id}>
                    {userDisplayName(j)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={saving} sx={{ color: "text.primary" }}>
          {t("judges.compositeTab.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          sx={{
            bgcolor: colors.foxOrange,
            "&:hover": { bgcolor: colors.foxOrange, filter: "brightness(0.92)" },
          }}
        >
          {saving ? t("judges.compositeTab.saving") : t("judges.compositeTab.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
